import type { AppError, RequestConfig } from '../types/api.types';
import { normalizeError, ERROR_CODES } from './errors';
import { csrfManager } from './csrf';
import {
  getAccessToken,
  updateAccessToken,
  clearSessionLocally,
  isLoggingOut,
  getStatus,
} from '@/features/auth/store/session.store';

/**
 * API Client
 *
 * Features:
 * - Base URL configuration via env
 * - Credentials: include (for HttpOnly cookies)
 * - CSRF token handling (X-CSRF-Token header)
 * - Request timeout and AbortController support
 * - Normalized error handling
 * - Single-flight token refresh on 401
 * - Retry-once on CSRF errors
 *
 * Security notes:
 * - Never logs request/response bodies that may contain secrets
 * - CSRF token refreshed automatically on 403
 * - 401 triggers session refresh (implemented in auth module)
 */

const API_BASE_URL = import.meta.env['VITE_API_BASE_URL'] || '';
const DEFAULT_TIMEOUT = 30000; // 30 seconds

// Track refresh in progress
let refreshPromise: Promise<void> | null = null;
let isRefreshing = false;

// Queue for requests waiting for refresh
type QueueItem = {
  resolve: (value: void) => void;
  reject: (error: unknown) => void;
};
const refreshQueue: QueueItem[] = [];

type RefreshFailureKind =
  | 'session_replaced'
  | 'unauthorized'
  | 'transient'
  | 'unknown';

class RefreshFailureError extends Error {
  kind: RefreshFailureKind;

  constructor(kind: RefreshFailureKind, message: string) {
    super(message);
    this.name = 'RefreshFailureError';
    this.kind = kind;
  }
}

function isRefreshAuthFailure(error: unknown): boolean {
  return (
    error instanceof RefreshFailureError &&
    (error.kind === 'unauthorized' || error.kind === 'session_replaced')
  );
}

/**
 * Create a timeout AbortController
 */
function createTimeoutController(timeout: number): AbortController {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), timeout);
  return controller;
}

/**
 * Attempt to refresh the session
 */
export async function refreshSession(): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch {
    throw new RefreshFailureError(
      'transient',
      'Session refresh failed due to network error'
    );
  }

  if (!response.ok) {
    // Check if the session was replaced by a new login (license enforcement)
    const errorJson = await safeParseJson(response);
    const rcode =
      (errorJson as Record<string, unknown>)?.['data'] &&
      typeof (errorJson as Record<string, unknown>)['data'] === 'object'
        ? (
            (errorJson as Record<string, unknown>)['data'] as Record<
              string,
              unknown
            >
          )['rcode']
        : undefined;

    if (rcode === 4105) {
      // Same gating as the 401 handler below: only a real "session replaced"
      // when we still consider ourselves authenticated.
      if (isLoggingOut() || getStatus() !== 'authenticated') {
        throw new RefreshFailureError('unauthorized', 'Logged out');
      }
      // Session replaced — clearSessionLocally with specific reason
      clearSessionLocally('session_replaced');
      throw new RefreshFailureError('session_replaced', 'Session replaced');
    }

    if (response.status === 401 || response.status === 403) {
      throw new RefreshFailureError(
        'unauthorized',
        'Session refresh unauthorized'
      );
    }

    if (response.status >= 500) {
      throw new RefreshFailureError(
        'transient',
        'Session refresh server error'
      );
    }

    throw new RefreshFailureError('unknown', 'Session refresh failed');
  }

  // Parse response and unwrap backend format: { isOk: true, data: { accessToken, csrfToken } }
  const jsonData = await response.json();
  const data = jsonData?.data || jsonData; // Unwrap if wrapped

  // Update access token in session store (memory)
  if (data.accessToken) {
    updateAccessToken(data.accessToken);
  }

  // CSRF token is already updated in cookie by backend
}

/**
 * Handle 401 by refreshing session (single-flight)
 */
async function handleUnauthorized(): Promise<void> {
  if (isRefreshing && refreshPromise) {
    // Wait for the existing refresh to complete
    return new Promise<void>((resolve, reject) => {
      refreshQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  refreshPromise = refreshSession();

  try {
    await refreshPromise;
    // Resolve all queued requests
    refreshQueue.forEach((item) => item.resolve());
    refreshQueue.length = 0;
  } catch (error) {
    // Reject all queued requests
    refreshQueue.forEach((item) => item.reject(error));
    refreshQueue.length = 0;
    if (isRefreshAuthFailure(error)) {
      clearSessionLocally();
    }
    throw error;
  } finally {
    isRefreshing = false;
    refreshPromise = null;
  }
}

/**
 * Make an API request with full error handling and retry logic
 */
async function request<T>(
  endpoint: string,
  config: RequestConfig = {},
  _retryCount: number = 0,
  _csrfRetried: boolean = false
): Promise<T> {
  const MAX_AUTH_RETRIES = 1;
  const {
    method = 'GET',
    body,
    headers = {},
    signal,
    timeout = DEFAULT_TIMEOUT,
    skipCsrf = false,
    skipAuth = false,
  } = config;

  // Create timeout controller if no signal provided
  const timeoutController = signal ? null : createTimeoutController(timeout);
  const effectiveSignal = signal || timeoutController?.signal;

  // Prepare headers
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  // Add Authorization header with access token (if available and not skipped)
  if (!skipAuth) {
    const accessToken = getAccessToken();
    if (accessToken) {
      requestHeaders['Authorization'] = `Bearer ${accessToken}`;
    }
  }

  // Add CSRF token for state-changing requests
  const isStateMutating = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
  if (isStateMutating && !skipCsrf) {
    try {
      // Read CSRF token from cookie (set by backend)
      const csrfToken = document.cookie
        .split('; ')
        .find((row) => row.startsWith('csrf-token='))
        ?.split('=')[1];

      if (csrfToken) {
        requestHeaders['X-CSRF-Token'] = csrfToken;
      } else {
        // Fallback: fetch from server if not in cookie
        const token = await csrfManager.getToken();
        if (token) {
          requestHeaders['X-CSRF-Token'] = token;
        }
      }
    } catch (error) {
      throw normalizeError(error);
    }
  }

  // Make the request
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'include',
      signal: effectiveSignal,
    });

    // Handle 401 - trigger session refresh and retry (skip for auth endpoints)
    if (response.status === 401 && !skipAuth) {
      // Check if this is a session-replaced response (rcode 4105) — no point refreshing
      const errorJson401 = await safeParseJson(response);
      const rcode401 =
        errorJson401 &&
        typeof errorJson401 === 'object' &&
        'data' in (errorJson401 as Record<string, unknown>) &&
        typeof (errorJson401 as Record<string, unknown>)['data'] === 'object'
          ? (
              (errorJson401 as Record<string, unknown>)['data'] as Record<
                string,
                unknown
              >
            )['rcode']
          : undefined;

      if (rcode401 === 4105) {
        // Suppress false-positive: a 4105 "session replaced by new login" is
        // only meaningful when there's actually an authenticated session being
        // displaced. If we're mid-logout, or the local session is already
        // gone, treat as a plain unauthorized — no banner.
        if (isLoggingOut() || getStatus() !== 'authenticated') {
          throw {
            message: 'Unauthorized',
            code: ERROR_CODES.UNAUTHORIZED,
            status: 401,
          } as AppError;
        }
        clearSessionLocally('session_replaced');
        throw {
          message: 'Session replaced by new login',
          code: ERROR_CODES.UNAUTHORIZED,
          status: 401,
          rcode: 4105,
        } as AppError;
      }

      // Don't auto-refresh for login/signup/refresh endpoints to avoid loops
      // BUT do auto-refresh for /auth/profile since it requires a valid token
      const isAuthMutationEndpoint =
        endpoint === '/auth/login' ||
        endpoint === '/auth/signup' ||
        endpoint === '/auth/refresh' ||
        endpoint === '/auth/logout' ||
        endpoint === '/auth/logout-all';

      if (!isAuthMutationEndpoint) {
        if (_retryCount < MAX_AUTH_RETRIES) {
          try {
            await handleUnauthorized();
            // Retry the original request with incremented counter
            return request<T>(endpoint, config, _retryCount + 1);
          } catch (refreshError) {
            if (isRefreshAuthFailure(refreshError)) {
              // Refresh failed due to invalid/expired session.
              // Return 401 so auth guards can redirect to login.
              const errorData = (await safeParseJson(response)) as Record<
                string,
                unknown
              > | null;
              throw {
                message: (errorData?.['message'] as string) || 'Unauthorized',
                code: ERROR_CODES.UNAUTHORIZED,
                status: 401,
              } as AppError;
            }

            // Refresh failed for a transient reason (network/server). Keep
            // session state and surface the operational error to the caller.
            throw normalizeError(refreshError);
          }
        } else {
          // Max retries exceeded - return 401 error
          const errorData = (await safeParseJson(response)) as Record<
            string,
            unknown
          > | null;
          throw {
            message: (errorData?.['message'] as string) || 'Unauthorized',
            code: ERROR_CODES.UNAUTHORIZED,
            status: 401,
          } as AppError;
        }
      } else {
        // For auth mutation endpoints, just throw the error without retry
        const errorData = (await safeParseJson(response)) as Record<
          string,
          unknown
        > | null;
        throw {
          message: (errorData?.['message'] as string) || 'Unauthorized',
          code: ERROR_CODES.UNAUTHORIZED,
          status: 401,
        } as AppError;
      }
    }

    // Handle 403 - might be CSRF error, fetch a fresh token and retry once
    // Parse body once so both CSRF check and generic handler can use it
    let prefetchedErrorData: unknown = undefined;
    if (
      response.status === 403 &&
      isStateMutating &&
      !skipCsrf &&
      !_csrfRetried
    ) {
      prefetchedErrorData = await safeParseJson(response);
      const topLevel = prefetchedErrorData as Record<string, unknown> | null;
      // Backend sends: { isOk: false, data: { rcode: 4303 } }
      const rcode = (topLevel?.['data'] as Record<string, unknown>)?.['rcode'];
      const isCsrfError = rcode === 4303;

      if (isCsrfError) {
        // Clear cached CSRF token so the retry fetches a fresh one from GET /auth/csrf
        csrfManager.clearToken();
        return request<T>(endpoint, config, _retryCount, true);
      }
    }

    // Handle other errors
    if (!response.ok) {
      // Reuse body if already parsed by the 403 CSRF check above
      const errorData =
        prefetchedErrorData !== undefined
          ? prefetchedErrorData
          : await safeParseJson(response);

      // Extract error from backend format: { isOk: false, data: { rcode } }
      // Backend only sends error code for security, no messages
      const backendData =
        errorData && typeof errorData === 'object' && 'data' in errorData
          ? (errorData as { data: Record<string, unknown> }).data
          : null;

      const errorCode =
        (backendData?.['rcode'] as string) || getErrorCode(response.status);

      const backendMessage =
        (backendData?.['message'] as string) ||
        (errorData &&
        typeof errorData === 'object' &&
        'message' in (errorData as Record<string, unknown>)
          ? ((errorData as Record<string, unknown>)['message'] as string)
          : '') ||
        '';

      throw {
        // Keep backend business-rule message when present; otherwise fallback to code mapping.
        message: backendMessage,
        code: errorCode,
        status: response.status,
        details: backendData?.['debug'], // Only in development
      } as AppError;
    }

    // Parse successful response
    if (response.status === 204) {
      return undefined as T;
    }

    const jsonData = await response.json();

    // Unwrap backend response format: { isOk: true, data: {...} }
    if (
      jsonData &&
      typeof jsonData === 'object' &&
      'isOk' in jsonData &&
      'data' in jsonData
    ) {
      return jsonData.data as T;
    }

    // Return as-is if not wrapped
    return jsonData as T;
  } catch (error) {
    // If it's already an AppError, re-throw it
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    // Otherwise normalize it
    throw normalizeError(error);
  }
}

/**
 * Safely parse JSON response
 */
async function safeParseJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Map HTTP status to error code
 */
function getErrorCode(status: number): string {
  switch (status) {
    case 401:
      return ERROR_CODES.UNAUTHORIZED;
    case 403:
      return ERROR_CODES.FORBIDDEN;
    case 404:
      return ERROR_CODES.NOT_FOUND;
    case 422:
      return ERROR_CODES.VALIDATION_ERROR;
    case 500:
    case 502:
    case 503:
      return ERROR_CODES.SERVER_ERROR;
    default:
      return ERROR_CODES.UNKNOWN;
  }
}

/**
 * Export API client methods
 */
export const apiClient = {
  get: <T>(endpoint: string, config?: Omit<RequestConfig, 'method' | 'body'>) =>
    request<T>(endpoint, { ...config, method: 'GET' }),

  post: <T>(
    endpoint: string,
    body?: unknown,
    config?: Omit<RequestConfig, 'method'>
  ) => request<T>(endpoint, { ...config, method: 'POST', body }),

  put: <T>(
    endpoint: string,
    body?: unknown,
    config?: Omit<RequestConfig, 'method'>
  ) => request<T>(endpoint, { ...config, method: 'PUT', body }),

  patch: <T>(
    endpoint: string,
    body?: unknown,
    config?: Omit<RequestConfig, 'method'>
  ) => request<T>(endpoint, { ...config, method: 'PATCH', body }),

  delete: <T>(
    endpoint: string,
    config?: Omit<RequestConfig, 'method' | 'body'>
  ) => request<T>(endpoint, { ...config, method: 'DELETE' }),
};
